import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { safeSerializeAdvancedFilter } from '../Filters/utils';
import { getIconForFieldName } from '../People/Filters/common';
import { getEnhancedDescriptionForFieldName } from '../People/Table/commons';
import TableCard from './TableCard';

const toAdvancedFilter = (
  dataKey: any,
  decodedFilterValue: string,
  description: string
) => {
  return {
    index: 0,
    type: 'GROUP',
    operator: 'OR',
    values: [
      {
        type: 'CONDITION',
        key: dataKey,
        values: [
          {
            name: decodedFilterValue,
            icon: getIconForFieldName(dataKey),
            description: description,
            filterObject: {
              key: dataKey,
              value: decodedFilterValue,
              keyDescription: description,
            },
          },
        ],
        operator: 'EQ',
        index: 101,
      },
    ],
  };
};

const buildPerformanceAdminLink = (dataKey, filterValue, formatMessage) => {
  // Too complicated for the time being, skip these fields
  if (dataKey === 'manager' || dataKey === 'manager_or_above') {
    return undefined;
  }

  const decodedFilterValue = decodeURIComponent(filterValue);
  const description = getEnhancedDescriptionForFieldName(
    dataKey,
    formatMessage
  );
  const filter = safeSerializeAdvancedFilter(
    toAdvancedFilter(dataKey, decodedFilterValue, description),
    formatMessage
  );
  return `/performance/admin?advanced_filter=${filter}}`;
};

const createNameObject = (stringOrJson, dataKey, formatMessage) => {
  let nameObj = {};
  let filterValue = '';
  try {
    const nameInfo = JSON.parse(stringOrJson);
    if (typeof nameInfo !== 'object') {
      throw new Error('Not a JSON, default');
    }
    nameObj = {
      name: nameInfo.full_name,
      sortKey: nameInfo.full_name,
    };
    filterValue = nameInfo.email || nameInfo.full_name || nameInfo.externalId;
  } catch (e) {
    nameObj = {
      name: stringOrJson,
      sortKey: stringOrJson,
    };
    filterValue = stringOrJson;
  }
  // @ts-expect-error
  nameObj.link = buildPerformanceAdminLink(
    dataKey,
    encodeURIComponent(filterValue),
    formatMessage
  );
  return nameObj;
};

type Props = {
  className?: string;
  data: Record<string, [number, number]>;
  dataKey: string;
  defaultSortBy?: string;
  emptyStateText?: string;
  fields: string[];
  role?: string;
  style?: Record<string, unknown>;
  title: string;
};

const ProgressTableCard: FC<Props> = ({ data = {}, ...props }) => {
  const { formatMessage } = useIntl();
  const tableRows = useMemo(
    () =>
      !data || Object.keys(data).length === 0
        ? []
        : Object.entries(data).map((x) => {
            const [nameStringOrJson, [count, total]] = x;
            const nameObject = createNameObject(
              nameStringOrJson,
              props.dataKey,
              formatMessage
            );
            // @ts-expect-error
            const pct = parseInt((100.0 * count) / total + 0.5);
            return [nameObject, count, total, pct];
          }),
    [data, props.dataKey, formatMessage]
  );

  return (
    <TableCard
      // @ts-expect-error
      className={props.className}
      // @ts-expect-error
      defaultSortBy={props.defaultSortBy}
      emptyStateText={props.emptyStateText}
      // @ts-expect-error
      fields={props.fields}
      rowData={tableRows}
      role={props.role}
      style={props.style}
      title={props.title}
    />
  );
};

export default React.memo(ProgressTableCard);
